import axios from 'axios';
import type { DashboardSummaryResponse, CurrentUsageSummaryResponse, BillingPortalResponse } from '../models/types';
import { getCsrfToken } from '../lib/auth';
import { notifyUnauthorized } from '../lib/auth-events';
import { getDevIdentityHeaders } from '../lib/dev-auth';

const api = axios.create({
  baseURL: import.meta.env.VITE_API_BASE_URL || '/api/v1',
  timeout: 30000,
  // Send the m365 session cookie on every request (same-origin /api proxy).
  withCredentials: true,
  headers: {
    'Content-Type': 'application/json',
  },
});

const MUTATING_METHODS = new Set(['post', 'put', 'patch', 'delete']);

// Convert PascalCase keys from .NET API to camelCase for frontend
function toCamelCase(obj: any): any {
  if (Array.isArray(obj)) return obj.map(toCamelCase);
  if (obj !== null && typeof obj === 'object' && !(obj instanceof Date)) {
    return Object.fromEntries(
      Object.entries(obj).map(([key, val]) => [
        key.charAt(0).toLowerCase() + key.slice(1),
        toCamelCase(val),
      ])
    );
  }
  return obj;
}

// Response interceptor: PascalCase → camelCase, error logging, 401 handling.
api.interceptors.response.use(
  response => {
    if (response.data) {
      response.data = toCamelCase(response.data);
    }
    return response;
  },
  error => {
    console.error('API Error:', {
      url: error.config?.url,
      status: error.response?.status,
      message: error.message,
      data: error.response?.data,
    });
    // Clear auth state on 401 so the app returns to the sign-in surface.
    if (error.response?.status === 401) {
      notifyUnauthorized();
    }
    return Promise.reject(error);
  }
);

// Request interceptor: inject the CSRF header on state-changing requests and
// forward dev-mode identity headers (no-op outside dev mode).
api.interceptors.request.use(config => {
  const method = (config.method || 'get').toLowerCase();
  if (MUTATING_METHODS.has(method)) {
    const csrf = getCsrfToken();
    if (csrf) {
      config.headers.set('X-CSRF-Token', csrf);
    }
  }
  const devHeaders = getDevIdentityHeaders();
  for (const [key, value] of Object.entries(devHeaders)) {
    config.headers.set(key, value);
  }
  return config;
});

export const dashboardApi = {
  /**
   * Fetch complete dashboard summary for an organization
   */
  getDashboardSummary: async (organizationId: string): Promise<DashboardSummaryResponse> => {
    const response = await api.get<DashboardSummaryResponse>(
      `/orgs/${organizationId}/dashboard/summary`
    );
    return response.data;
  },

  /**
   * Health check endpoint
   */
  healthCheck: async (): Promise<{ status: string }> => {
    const response = await api.get('/health');
    return response.data;
  },

  /**
   * Get Stripe Customer Portal URL for self-service billing management
   */
  getBillingPortalUrl: async (organizationId: string): Promise<BillingPortalResponse> => {
    const response = await api.get<BillingPortalResponse>(
      `/orgs/${organizationId}/billing/portal`
    );
    return response.data;
  },

  /**
   * Get current billing period usage summary with meter breakdowns.
   * Maps the flat API response to the nested shape BillingPage expects.
   */
  getCurrentUsageSummary: async (organizationId: string): Promise<CurrentUsageSummaryResponse> => {
    const response = await api.get(`/orgs/${organizationId}/usage/current`);
    const d = response.data as any;
    const included = d.ratePlan?.includedTokens ?? d.totalTokensGranted ?? 0;
    const used = d.totalTokensConsumed ?? 0;
    const remaining = Math.max(0, included - used);
    const overage = d.overageTokens ?? Math.max(0, used - included);
    const overageCost = d.overageCost ?? (overage * (d.ratePlan?.overageRatePerThousand ?? 0.01) / 1000);
    return {
      organizationId: d.organizationId ?? organizationId,
      billingPeriodStart: d.periodStart ?? new Date().toISOString(),
      billingPeriodEnd: d.updatedAt ?? new Date().toISOString(),
      tokenUsage: { included, used, remaining, overage, overageCostEstimate: overageCost },
      meterBreakdowns: [],
    };
  },
};
